import { normalizeLocationKey } from './locationKey';

/** The only fields we need off a 1Office master state row. */
export interface MasterStateRow {
  stateName?: string;
  regionName?: string;
}

/**
 * Index the 1Office state master by NORMALIZED state name -> region.
 *
 * 1Office is the only authoritative state->region mapping we have: every state row
 * carries `regionId`/`regionName` (e.g. Bihar -> "East"). Rows without a region are
 * skipped rather than bucketed, so a missing region stays visibly missing.
 */
export const buildRegionByStateKey = (states: readonly MasterStateRow[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const s of states) {
    const name = (s.stateName || '').trim();
    if (name && s.regionName) map.set(normalizeLocationKey(name), s.regionName);
  }
  return map;
};

/**
 * Full-name synonyms for states whose official name changed, or that the 1Office master
 * and bulk uploads spell differently (the master says "Pondicherry"; uploads bring the
 * modern "Puducherry"). Tried only when a state has no master row under its own name.
 *
 * FULL NAMES ONLY — no "PY" / "UT" style codes: this lookup is applied silently, so a
 * store whose state is literally "UT" must stay unmapped rather than quietly become
 * Uttarakhand. Mirrors the `names` of STATE_ALIAS_GROUPS in
 * apps/api/src/utils/location-aliases.ts (no shared package) — keep the two in step.
 */
const STATE_SYNONYM_GROUPS: readonly (readonly string[])[] = [
  ['Delhi', 'NCT of Delhi', 'National Capital Territory of Delhi', 'NCT Delhi'],
  ['Odisha', 'Orissa'],
  ['Puducherry', 'Pondicherry'],
  ['Uttarakhand', 'Uttaranchal'],
  ['Jammu and Kashmir', 'Jammu Kashmir'],
  ['Andaman and Nicobar Islands', 'Andaman and Nicobar'],
  ['Chhattisgarh', 'Chattisgarh'],
];

const STATE_SYNONYM_KEYS = new Map<string, string[]>();
for (const group of STATE_SYNONYM_GROUPS) {
  const keys = group.map((name) => normalizeLocationKey(name));
  for (const key of keys) STATE_SYNONYM_KEYS.set(key, keys.filter((k) => k !== key));
}

/**
 * The keys a stored state may match in the master: its own key first, then its known
 * full-name synonyms. Empty for a blank name. Never fuzzy.
 */
export const stateMatchKeys = (stateRaw?: string | null): string[] => {
  const key = normalizeLocationKey(stateRaw);
  return key ? [key, ...(STATE_SYNONYM_KEYS.get(key) ?? [])] : [];
};

/**
 * Resolve a (possibly dirty) stored state string to its region, or `undefined` when the
 * state has no master row.
 *
 * Matching goes through `normalizeLocationKey`, so casing, padding and punctuation
 * variants — "KERALA", " Kerala", "Jammu & Kashmir" vs "Jammu and Kashmir" — all land on
 * the same master row. A state with no master row of its own is then tried under its
 * full-name synonyms (see STATE_SYNONYM_GROUPS), whichever spelling the master carries.
 *
 * ⚠️ Returns `undefined` rather than defaulting to a bucket. The hardcoded map this
 * replaced ended in `return 'west'`, which silently misfiled every state it didn't list
 * (Gujarat, Himachal, Chandigarh, the entire Northeast) as West.
 */
export const regionForStateName = (
  regionByStateKey: ReadonlyMap<string, string>,
  stateRaw?: string | null,
): string | undefined => {
  for (const key of stateMatchKeys(stateRaw)) {
    const region = regionByStateKey.get(key);
    if (region) return region;
  }
  return undefined;
};
